import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Warehouse, Store, Layers, Loader2, Eraser, ShieldCheck } from "lucide-react";

const SCOPES = [["depo", "Yalnız Depo", Warehouse], ["magaza", "Yalnız Mağaza", Store], ["both", "Depo + Mağaza", Layers]];

function Preview({ pv, scope }) {
  if (!pv) return <div className="text-xs text-slate-400 flex items-center gap-2 py-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Hesaplanıyor…</div>;
  if (!pv.products) return <div className="text-sm text-slate-500 py-2" data-testid="bulk-zero-nothing">Seçili ürünlerde bu bölümde sıfırlanacak stok yok.</div>;
  const row = (loc, l) => (scope === loc || scope === "both") && <div className="flex justify-between"><span>{l}</span><b className="tnum">{NUM(pv[`${loc}_n`])} ürün · {NUM(pv[`${loc}_qty`])} birim → 0</b></div>;
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1" data-testid="bulk-zero-preview">
      {row("depo", "Depo")}
      {row("magaza", "Mağaza")}
      {pv.neg_n > 0 && <div className="text-red-700">{NUM(pv.neg_n)} eksi stok da 0'a çekilecek.</div>}
    </div>
  );
}

/** Sayım öncesi toplu stok sıfırlama — bölüm her seferinde sorulur; hareket geçmişi silinmez. */
export function BulkZeroDialog({ open, ids, onCancel, onDone }) {
  const [scope, setScope] = useState(null);
  const [pv, setPv] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const big = ids.length > 10;
  useEffect(() => { if (!open) { setScope(null); setPv(null); setWord(""); } }, [open]);
  useEffect(() => {
    if (!open || !scope) return undefined;
    let live = true;
    setPv(null);
    api.post("/stock/bulk-zero", { ids, scope, dry_run: true }).then((r) => { if (live) setPv(r.data); }).catch((e) => toast.error(apiError(e)));
    return () => { live = false; };
  }, [open, scope, ids]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/stock/bulk-zero", { ids, scope }); onDone(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const ok = scope && pv?.products > 0 && (!big || word.trim().toLocaleUpperCase("tr").replace(/İ/g, "I") === "SIFIRLA");
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-zero-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Eraser className="h-5 w-5 text-red-600" />Stok Sıfırla · {NUM(ids.length)} ürün</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="text-sm font-semibold text-slate-700">Hangi bölüm sıfırlansın?</div>
          <div className="grid grid-cols-3 gap-2">
            {SCOPES.map(([v, l, Icon]) => (
              <button key={v} type="button" onClick={() => setScope(v)} className={cn("rounded-xl border p-3 text-center transition-colors flex flex-col items-center gap-1.5", scope === v ? "border-red-500 bg-red-50 ring-1 ring-red-500 text-red-800" : "hover:bg-slate-50 text-slate-700")} data-testid={`bulk-zero-scope-${v}`}>
                <Icon className="h-5 w-5" /><span className="text-xs font-bold">{l}</span>
              </button>
            ))}
          </div>
          {scope && <Preview pv={pv} scope={scope} />}
          {scope && big && pv?.products > 0 && <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder='Onay için "SIFIRLA" yazın' data-testid="bulk-zero-confirm-word" />}
          <p className="text-[11px] text-slate-500 flex gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />Stok hareketleri silinmez; her bölüm için "sayım → 0" hareketi kaydedilir ve "Geri al" ile tamamı geri çevrilebilir.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Vazgeç</Button>
          <Button onClick={run} disabled={busy || !ok} className="bg-red-600 hover:bg-red-700" data-testid="bulk-zero-confirm">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sıfırla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
